// The ServiceTitan move's reconciliation (migration.reconciliation): the sample and each year checked
// against ServiceTitan, every check green or red with its numbers. The owner signs off the sample (not
// binding: a fair basis for the data model) and each year (binding), by typing their full name
// (migration.signOffSample, migration.signOffYear). A year is signed only when every check is green.

import { useCallback, useEffect, useState } from 'react';
import { client } from '../api.ts';
import { Empty, Loading, Panel, errorText, useQuery } from '../books/ui.tsx';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import {
  type PartProps,
  holds,
  OWNER_PERMS,
  signOffBlocks,
  yearLabel,
  yearsOf,
  type Report,
  type Status,
} from './model.ts';

export function Reconcile({ held, toast, timeZone }: PartProps) {
  const status = useQuery<Status>('migration.status', {});
  const [year, setYear] = useState(0);
  if (!status.data)
    return <Loading error={status.error} what="the years" retry={() => void status.reload()} />;
  const years = yearsOf(status.data.run);
  return (
    <>
      <div className="pills" role="group" aria-label="Which year">
        {years.map((y) => (
          <button key={y} type="button" className="pill" aria-pressed={year === y} onClick={() => setYear(y)}>
            {yearLabel(y)}
            {status.data!.yearsSigned.includes(y) ? ' ✓' : ''}
          </button>
        ))}
      </div>
      <YearReport
        key={year}
        year={year}
        held={held}
        toast={toast}
        timeZone={timeZone}
        onSigned={() => void status.reload()}
      />
    </>
  );
}

function YearReport({
  year,
  held,
  toast,
  timeZone,
  onSigned,
}: PartProps & { year: number; onSigned: () => void }) {
  // migration.reconciliation answers null for a year not reconciled yet: kept apart from "not here yet"
  const [r, setR] = useState<Report | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setR(await client.call<Report | null>('migration.reconciliation', { year }));
      setLoadError(null);
    } catch (e) {
      setLoadError(errorText(e, 'Could not load the reconciliation.'));
    }
  }, [year]);
  useEffect(() => {
    void reload();
  }, [reload]);
  const q = { reload };
  const [signing, setSigning] = useState(false);
  const owner = holds(held, OWNER_PERMS);
  if (r === undefined)
    return <Loading error={loadError} what="the reconciliation" retry={() => void reload()} />;
  const blocks = signOffBlocks(r);
  const at = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  return (
    <Panel
      title={year === 0 ? 'The sample' : `${year}`}
      aside={
        r ? (
          <span className={`chip say ${r.signedOff ? 'ok' : r.allGreen ? 'ok' : 'bad'}`}>
            {r.signedOff ? 'Signed off' : r.allGreen ? 'All green' : 'Not matching yet'}
          </span>
        ) : undefined
      }
    >
      {!r ? (
        <Empty>
          Not reconciled yet. Each year is reconciled right after a load: ask for one from the overview, with
          the year’s Load button.
        </Empty>
      ) : (
        <>
          <p className="muted small">Reconciled {at(r.computedAt)}</p>
          <div className="table-wrap">
            <table className="grid-table" aria-label={`${yearLabel(year)}: the checks`}>
              <thead>
                <tr>
                  <th>Check</th>
                  <th className="num">ServiceTitan</th>
                  <th className="num">Flowology</th>
                  <th>Matches</th>
                </tr>
              </thead>
              <tbody>
                {r.checks.map((c) => (
                  <tr key={c.key}>
                    <td>
                      {c.name}
                      {c.note && <span className="muted small block">{c.note}</span>}
                    </td>
                    <td className="num">{c.expected ?? '–'}</td>
                    <td className="num">{c.actual ?? '–'}</td>
                    <td>
                      <span className={`chip say ${c.ok ? 'ok' : 'bad'}`}>{c.ok ? 'Yes' : 'No'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.signedOff ? (
            <p className="notice ok">
              Signed off by {r.signedOff.typedName}, {at(r.signedOff.at)}
              {r.signedOff.binding ? ' (binding).' : ' (not binding: the sample).'}
            </p>
          ) : owner ? (
            <div className="row-actions">
              <button
                type="button"
                className="btn primary"
                disabled={!!blocks.length}
                onClick={() => setSigning(true)}
              >
                {year === 0 ? 'Sign off the sample' : `Sign off ${year}`}
              </button>
              {blocks.length > 0 && <span className="muted small">First: {blocks.join(' ')}</span>}
            </div>
          ) : (
            <p className="muted small">The owner signs it off.</p>
          )}
        </>
      )}
      {signing && r && (
        <SignOff
          year={year}
          onClose={() => setSigning(false)}
          onDone={async (statement) => {
            toast(statement);
            setSigning(false);
            await q.reload();
            onSigned();
          }}
        />
      )}
    </Panel>
  );
}

function SignOff({
  year,
  onClose,
  onDone,
}: {
  year: number;
  onClose: () => void;
  onDone: (statement: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sign = async () => {
    setBusy(true);
    setError(null);
    try {
      const r =
        year === 0
          ? await client.call<{ statement: string }>('migration.signOffSample', { typedName: name.trim() })
          : await client.call<{ statement: string }>('migration.signOffYear', {
              year,
              typedName: name.trim(),
            });
      await onDone(r.statement);
    } catch (e) {
      // e.g. "Data was loaded after this report. Reconcile again before signing."
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title={year === 0 ? 'Sign off the sample?' : `Sign off ${year}?`}
      outcome={
        year === 0
          ? 'The sample matches ServiceTitan and is a fair basis for the data model. Not binding; each year is signed off on its own.'
          : `The ${year} data in Flowology matches ServiceTitan as shown in this report. This sign-off is binding.`
      }
      confirm={year === 0 ? 'Sign off the sample' : `Sign off ${year}`}
      ready={name.trim().length > 1}
      busy={busy}
      error={error}
      onConfirm={() => void sign()}
      onClose={onClose}
    >
      <label>
        Type your full name to sign
        <input className="field" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
    </MoneySheet>
  );
}
